import { useMemo } from "react";

import { Box, Tooltip } from "@mantine/core";
import { Calendar } from "@mantine/dates";
import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/sr";

import i18n from "config/localization/i18n";
import { useCalendarEvents } from "hooks";
import {
  DashboardCalendarParams,
  DashboardCalendarResponse,
} from "types";
import { CalendarEventUtils, DateUtils } from "utils";

import classes from "../agent/CalendarSection.module.css";

const WEEKEND_TEXT_COLOR = "var(--mantine-color-red-6)";

interface DashboardCalendarSectionProps {
  className?: string;
  compact?: boolean;
  loadCalendar: (params: DashboardCalendarParams) => Promise<DashboardCalendarResponse>;
  selectedAgentIds?: number[] | null;
}

const isWeekendDay = (date: Date) => date.getDay() === 0 || date.getDay() === 6;

const formatWeekdayLabel = (date: Date, locale: string) => {
  const label = dayjs(date).locale(locale).format("dd");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const DashboardCalendarSection = ({
  className,
  compact = false,
  loadCalendar,
  selectedAgentIds,
}: DashboardCalendarSectionProps) => {
  const calendarLocale = i18n.language.toLowerCase().split("-")[0];
  const requestLoader = useMemo(
    () => (params: DashboardCalendarParams) => loadCalendar({
      ...params,
      ...(selectedAgentIds?.length ? { agentIds: selectedAgentIds } : {}),
    }),
    [loadCalendar, selectedAgentIds],
  );
  const { displayedDate, events, setDisplayedDate } = useCalendarEvents(requestLoader);
  const dayDetailsByDate = useMemo(
    () => CalendarEventUtils.buildCalendarDayDetails(events),
    [events],
  );

  return (
    <div
      className={[
        classes.root,
        compact ? classes.compactRoot : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Box className={classes.calendarShell}>
        <Calendar
          static
          date={displayedDate}
          onDateChange={setDisplayedDate}
          highlightToday={false}
          locale={calendarLocale}
          size={compact ? "xs" : "lg"}
          withCellSpacing={!compact}
          weekendDays={[0, 6]}
          maxLevel="month"
          minLevel="month"
          styles={
            compact
              ? {
                  calendarHeader: {
                    marginBottom: 2,
                    minHeight: 24,
                  },
                  calendarHeaderControl: {
                    width: 22,
                    minWidth: 22,
                    height: 22,
                    padding: 0,
                  },
                  calendarHeaderControlIcon: {
                    width: 14,
                    height: 14,
                  },
                  calendarHeaderLevel: {
                    fontSize: "13px",
                    fontWeight: 500,
                    lineHeight: 1.1,
                  },
                  weekdaysRow: {
                    marginBottom: 0,
                  },
                  weekday: {
                    fontSize: "11px",
                    fontWeight: 500,
                    paddingBottom: 1,
                  },
                }
              : undefined
          }
          hideOutsideDates={compact}
          weekdayFormat={(date) => {
            const label = formatWeekdayLabel(date, calendarLocale);

            if (!isWeekendDay(date)) {
              return label;
            }

            return <span className={classes.weekendWeekday}>{label}</span>;
          }}
          getDayProps={(date) => {
            const dateString = DateUtils.formatDateLocal(date);
            const details = dayDetailsByDate.get(dateString);
            const isMarked = Boolean(details?.background);
            const todayString = DateUtils.formatDateLocal(new Date());
            const isToday = dateString === todayString;
            const isWeekend = isWeekendDay(date);

            return {
              style: {
                background: details?.background,
                border:
                  isToday && !isMarked
                    ? "1.5px solid var(--mantine-color-gray-7)"
                    : "1.5px solid transparent",
                borderRadius: 8,
                color: isMarked
                  ? "var(--mantine-color-white)"
                  : isWeekend
                    ? WEEKEND_TEXT_COLOR
                    : undefined,
                fontWeight: isToday || isMarked ? 600 : 500,
              },
            };
          }}
          renderDay={(date) => {
            const dateString = DateUtils.formatDateLocal(date);
            const details = dayDetailsByDate.get(dateString);
            const dayContent = <div className={classes.dayCell}>{date.getDate()}</div>;

            if (!details?.tooltipLabels.length) {
              return dayContent;
            }

            return (
              <Tooltip
                label={(
                  <div className={classes.tooltipContent}>
                    {details.tooltipLabels.map((label) => (
                      <div key={label} className={classes.tooltipLine}>
                        {label}
                      </div>
                    ))}
                  </div>
                )}
                multiline
                position="top"
                withArrow
                withinPortal={false}
              >
                <div className={classes.dayTooltipTarget}>{dayContent}</div>
              </Tooltip>
            );
          }}
        />
      </Box>
    </div>
  );
};

export default DashboardCalendarSection;
